import React from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import RootLayout from './layouts/RootLayout';
import RootErrorBoundary from './components/RootErrorBoundary';
import { ErrorBoundary } from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import BlogPostWrapper from './components/BlogPostWrapper';
import DocsWrapper from './components/DocsWrapper';
import LangWrapper, { LANG_PREFIXES } from './components/LangWrapper';
import LangRedirect from './components/LangRedirect';

// Lazy-load all page components
const Index = React.lazy(() => import('./pages/Index'));
const Auth = React.lazy(() => import('./pages/Auth'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));
// v1.7.4 SEO sprint — new landing pages capturing existing impression demand
const LensFOVGuide = React.lazy(() => import('./pages/lens/LensFOVGuide'));
const CameraPage = React.lazy(() => import('./pages/camera/CameraPage'));
const NVRPage = React.lazy(() => import('./pages/nvr/NVRPage'));

// Landing pages
const CamerasIndex = React.lazy(() => import('./pages/CamerasIndex'));

// Comparison pages
const LocationLandingTemplate = React.lazy(() => import('./pages/location/LocationLandingTemplate'));
import { EN_CITIES, PL_CITIES, EN_CITIES_EXTENDED, DE_CITIES, IT_CITIES, ES_CITIES, CS_CITIES, PT_BR_CITIES, NL_CITIES, AU_CITIES, ZA_CITIES, HU_CITIES, LT_CITIES, RO_CITIES } from './data/cityLocations';
const SkuComparisonPage = React.lazy(() => import('./pages/compare/SkuComparisonPage'));

// Standards landing pages
const EN62676Calculator = React.lazy(() => import('./pages/standards/EN62676Calculator'));
const JohnsonCriteriaCalculator = React.lazy(() => import('./pages/standards/JohnsonCriteriaCalculator'));
const NDAACompliance = React.lazy(() => import('./pages/standards/NDAACompliance'));

// Vertical landing pages

// SEO blog posts targeting JVSG long-tail

// Day-4 sprint: lifestyle/legal long-tail blog posts (privacy, GDPR, ANPR technical, tender)

// Vendor-bypass alternative landing pages (Day-2 sprint additions, multi-vendor positioning)
// Phase 5 — detection-sensor SEO landings (Optex catalog integration)

// Vertical JVSG-alternative landing pages (programmatic exact-match URLs)

// IPVM-alternative + CCTVDesignTool-alternative landings (v1.7.5 SEO sprint)

// Calculators
const Calculators = React.lazy(() => import('./pages/Calculators'));
const FOVCalculator = React.lazy(() => import('./pages/calculator/FOVCalculator'));
const StorageCalculator = React.lazy(() => import('./pages/calculator/StorageCalculator'));
const UPSCalculator = React.lazy(() => import('./pages/calculator/UPSCalculator'));
const PixelDensityCalculator = React.lazy(() => import('./pages/calculator/PixelDensityCalculator'));
const IFOVCalculator = React.lazy(() => import('./pages/calculator/IFOVCalculator'));
const CameraQuantityCalculator = React.lazy(() => import('./pages/calculator/CameraQuantityCalculator'));
const LensSelectorCalculator = React.lazy(() => import('./pages/calculator/LensSelectorCalculator'));
const DORICalculator = React.lazy(() => import('./pages/calculator/DORICalculator'));
const OODPCVSCalculator = React.lazy(() => import('./pages/calculator/OODPCVSCalculator'));
const BandwidthCalculator = React.lazy(() => import('./pages/calculator/BandwidthCalculator'));
const PIRSensorsCalculator = React.lazy(() => import('./pages/calculator/PIRSensorsCalculator'));
const AlarmZonesCalculator = React.lazy(() => import('./pages/calculator/AlarmZonesCalculator'));
const BeamBarrierCalculator = React.lazy(() => import('./pages/calculator/BeamBarrierCalculator'));
const SystemBudgetCalculator = React.lazy(() => import('./pages/calculator/SystemBudgetCalculator'));
const CableLengthCalculator = React.lazy(() => import('./pages/calculator/CableLengthCalculator'));
const PoECalculator = React.lazy(() => import('./pages/calculator/PoECalculator'));
const LensCalculatorLanding = React.lazy(() => import('./pages/calculator/LensCalculatorLanding'));
const StorageCalculatorLanding = React.lazy(() => import('./pages/calculator/StorageCalculatorLanding'));
const BandwidthCalculatorLanding = React.lazy(() => import('./pages/calculator/BandwidthCalculatorLanding'));

// One-Stop-Shop transactional pages (Buy + Install pillars). CSR-only —
// excluded from SSG in vite.config.ts (auth-dependent data + lazy Leaflet).
const InstallerJobs = React.lazy(() => import('./pages/InstallerJobs'));
const OrderInstallation = React.lazy(() => import('./pages/OrderInstallation'));
const EquipmentCart = React.lazy(() => import('./pages/EquipmentCart'));

// Other pages
const Pricing = React.lazy(() => import('./pages/PricingPage'));
const RODO = React.lazy(() => import('./pages/RODO'));
const Changelog = React.lazy(() => import('./pages/Changelog'));
const Glossary = React.lazy(() => import('./pages/Glossary'));
const CheckoutSuccess = React.lazy(() => import('./pages/CheckoutSuccess'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const OrgAdminConsole = React.lazy(() => import('./pages/OrgAdminConsole'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
const CCTVDesignerV2 = React.lazy(() => import('./components/cctv-designer-v2/CCTVDesignerV2'));
const TrySandbox = React.lazy(() => import('./pages/TrySandbox'));
const Portal = React.lazy(() => import('./pages/Portal'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs'));
const CameraPhysics = React.lazy(() => import('./pages/CameraPhysics'));
const AttributionsPage = React.lazy(() => import('./pages/AttributionsPage'));
const Unsubscribed = React.lazy(() => import('./pages/Unsubscribed'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// CCTV Layout programmatic pages

// Docs pages
const Docs = React.lazy(() => import('./pages/Docs'));
const DocsGettingStarted = React.lazy(() => import('./pages/docs/GettingStarted'));
const DocsCreatingAProject = React.lazy(() => import('./pages/docs/CreatingAProject'));
const DocsUploadingFloorPlan = React.lazy(() => import('./pages/docs/UploadingFloorPlan'));
const DocsAddingCameras = React.lazy(() => import('./pages/docs/AddingCameras'));
const DocsFieldOfView = React.lazy(() => import('./pages/docs/FieldOfView'));
const DocsCableRouting = React.lazy(() => import('./pages/docs/CableRouting'));
const DocsPdfExport = React.lazy(() => import('./pages/docs/PdfExport'));
const DocsStorageCalculator = React.lazy(() => import('./pages/docs/StorageCalculatorDoc'));
const DocsManagingProjects = React.lazy(() => import('./pages/docs/ManagingProjects'));
const DocsSubscriptionPlans = React.lazy(() => import('./pages/docs/SubscriptionPlans'));

// Blog post slugs
const BLOG_SLUGS = [
  'how-to-design-cctv-system',
  'field-of-view-explained',
  'dori-in-cctv',
  'camera-placement-guide',
  'night-vision-cctv',
  'ip-vs-analog-cctv',
  'frame-rate-guide',
  'pixel-density-explained',
  'choosing-lens',
  'cctv-wiring',
  'false-alarm-reduction',
  'cctv-maintenance',
  'how-many-cameras',
  'blind-spots',
  'megapixel-distance',
  'warehouse-cctv',
  'home-security-cctv',
  '4k-vs-1080p-cctv',
  'poe-vs-wifi-cameras',
  'cctv-in-conflict-zones',
  'cctv-cost-estimation',
  'best-cctv-cameras-2026',
  'retail-store-cctv-layout',
  'parking-lot-cctv-layout',
  'how-to-create-cctv-proposal',
  'school-cctv-security-guide',
  'cctv-storage-calculation-guide',
  'cctv-camera-types-dome-bullet-ptz',
  'apartment-building-cctv-guide',
  'small-business-cctv-guide',
  'ai-cameras-edge-detection',
  'jvsg-pricing-explained-2026',
  'switching-from-jvsg-2026-migration-guide',
  'best-free-cctv-design-software-2026',
  'en-62676-4-oodpcvs-update-2026',
  'russian-software-eu-cctv-compliance-review-2026',
  'dori-calculation-walkthrough-2026',
  // Day-4 sprint: lifestyle/legal long-tail
  'can-my-neighbor-record-me-with-cctv-2026',
  'cctv-workplace-monitoring-gdpr-rodo-2026',
  'license-plate-camera-distance-and-focal-length-guide',
  'home-cctv-installation-legal-checklist-2026',
  'gdpr-cctv-signage-and-pictogram-requirements',
  'cctv-recording-retention-period-by-country',
  'commercial-cctv-design-tender-checklist-2026',
  // v1.7.4 SEO sprint
  'cctv-installation-best-practices',
  'dome-cctv-camera-field-of-view-specifications',
  'cctv-camera-terminology',
  // v1.7.5 SEO sprint — IPVM + CCTVDesignTool alternative supporting blogs
  'ipvm-system-design-tool-review-2026',
  'cctvdesigntool-com-review-2026',
  'switching-from-ipvm-or-cctvdesigntool-2026',
  // 2026-05-20 SEO sprint — vertical-pillar supporting blog
  'bank-cctv-design-2026',
];

// Lens focal lengths for /lens/:mm routes
const LENS_MM_VALUES = ['2.8', '3.6', '4', '6', '8', '12', '16', '25', '35', '50'];

// CCTV Layout building types
const CCTV_LAYOUT_TYPES = [
  'warehouse', 'school', 'office', 'retail-store', 'parking-lot', 'hospital',
  'hotel', 'restaurant', 'gas-station', 'bank', 'construction-site', 'apartment-building',
];

// Camera slugs — lazy-loaded to avoid pulling 40K-line cameras.ts into main bundle.
// getStaticPaths is only called during SSG build, not at CSR runtime.
const getAllCameraSlugs = () => import('@/lib/cameraSlug').then(m => m.getAllCameraSlugs());
const getAllNvrSlugs = () => import('@/lib/nvrSlug').then(m => m.getAllNvrSlugs());

// Docs slugs
const DOCS_SLUGS = [
  'getting-started',
  'creating-a-project',
  'uploading-floor-plan',
  'adding-cameras',
  'field-of-view',
  'cable-routing',
  'pdf-export',
  'storage-calculator',
  'managing-projects',
  'subscription-plans',
];

const DOCS_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  'getting-started': DocsGettingStarted,
  'creating-a-project': DocsCreatingAProject,
  'uploading-floor-plan': DocsUploadingFloorPlan,
  'adding-cameras': DocsAddingCameras,
  'field-of-view': DocsFieldOfView,
  'cable-routing': DocsCableRouting,
  'pdf-export': DocsPdfExport,
  'storage-calculator': DocsStorageCalculator,
  'managing-projects': DocsManagingProjects,
  'subscription-plans': DocsSubscriptionPlans,
};

// Map blog slug to component

export const routes: RouteObject[] = [
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      // === Public static routes ===
      { index: true, element: <Index /> },
      { path: 'auth', element: <Auth /> },
      { path: 'feedback', element: <FeedbackPage /> },

      // === Docs ===
      { path: 'docs', element: <Docs /> },

      // Doc articles (English only — language via useLanguage hook)
      ...DOCS_SLUGS.map(slug => ({
        path: `docs/${slug}`,
        element: <DocsWrapper slug={slug} englishComponent={DOCS_COMPONENTS[slug]} />,
      })),

      // === Docs language routes (/:lang/docs/...) ===
      ...LANG_PREFIXES.flatMap(lang => [
        { path: `${lang}/docs`, element: <LangWrapper><Docs /></LangWrapper> },
        ...DOCS_SLUGS.map(slug => ({
          path: `${lang}/docs/${slug}`,
          element: <LangWrapper><DocsWrapper slug={slug} englishComponent={DOCS_COMPONENTS[slug]} /></LangWrapper>,
        })),
      ]),

      // === Lens guides (dynamic) ===
      {
        path: 'lens/:mm',
        element: <LensFOVGuide />,
        getStaticPaths: () => LENS_MM_VALUES.map(mm => `lens/${mm}`),
      },

      // === Camera pages (programmatic SEO) ===
      {
        path: 'camera/:slug',
        element: <CameraPage />,
        getStaticPaths: getAllCameraSlugs,
      },

      // === NVR / recorder pages (programmatic SEO) ===
      // Only verified+datasheetUrl entries publish — anti-hallucination.
      {
        path: 'nvr/:slug',
        element: <NVRPage />,
        getStaticPaths: getAllNvrSlugs,
      },

      // === One-Stop-Shop: Buy + Install pillars ===
      { path: 'installer-jobs', element: <InstallerJobs /> },
      { path: 'order-installation', element: <OrderInstallation /> },
      { path: 'equipment-cart', element: <EquipmentCart /> },

      // === Calculators ===
      { path: 'calculators', element: <Calculators /> },
      { path: 'calculator/fov', element: <FOVCalculator /> },
      { path: 'calculator/storage', element: <StorageCalculator /> },
      { path: 'calculator/ups', element: <UPSCalculator /> },
      { path: 'calculator/pixel-density', element: <PixelDensityCalculator /> },
      { path: 'calculator/how-many-cameras', element: <CameraQuantityCalculator /> },
      { path: 'calculator/lens-selector', element: <LensSelectorCalculator /> },
      { path: 'calculator/ifov', element: <IFOVCalculator /> },
      { path: 'calculator/dori', element: <DORICalculator /> },
      { path: 'calculator/oodpcvs', element: <OODPCVSCalculator /> },
      { path: 'calculator/bandwidth', element: <BandwidthCalculator /> },
      { path: 'calculator/pir-sensors', element: <PIRSensorsCalculator /> },
      { path: 'calculator/alarm-zones', element: <AlarmZonesCalculator /> },
      { path: 'calculator/beam-barrier', element: <BeamBarrierCalculator /> },
      { path: 'calculator/system-budget', element: <SystemBudgetCalculator /> },
      { path: 'calculator/cable-length', element: <CableLengthCalculator /> },
      { path: 'calculator/poe', element: <PoECalculator /> },
      // SEO landing pages (2026-05-24) — root-slug versions of high-volume
      // calculator queries. Distinct from /calculator/* (the canonical
      // calculator namespace) — these target the literal head terms
      // "lens calculator cctv", "cctv storage calculator", "cctv bandwidth
      // calculator" that competitors rank for with deep-link slugs.
      { path: 'lens-calculator', element: <LensCalculatorLanding /> },
      { path: 'storage-calculator', element: <StorageCalculatorLanding /> },
      { path: 'bandwidth-calculator', element: <BandwidthCalculatorLanding /> },

      // === SEO landing pages ===
      { path: 'cameras', element: <CamerasIndex /> },


      // === Location SEO (EN cities — UK + US + AU + ZA) ===
      // 30 original UK+US + 5 Australia + 3 South Africa = 38 EN-locale
      // city pages under root (no language prefix — locale is en).
      ...EN_CITIES_EXTENDED.map(c => ({
        path: `cctv-design-${c.slug}`,
        element: <LocationLandingTemplate citySlug={c.slug} />,
      })),

      // === Location SEO (PL cities, /pl prefix only) ===
      // 30 Polish cities under /pl/. Wrapped in LangWrapper so the page
      // renders with PL locale active even if visitor lands without
      // hreflang trail.
      ...PL_CITIES.map(c => ({
        path: `pl/projekt-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — German cities (Sprint A) ===
      // 10 DE cities under /de/cctv-planung-{slug}. Largest underserved
      // EU paid-customer market (3 paid, 0 city pages before).
      ...DE_CITIES.map(c => ({
        path: `de/cctv-planung-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Italian cities (Sprint D) ===
      ...IT_CITIES.map(c => ({
        path: `it/progettazione-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Spanish cities (Sprint D) ===
      ...ES_CITIES.map(c => ({
        path: `es/diseno-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Czech cities (Sprint C) ===
      ...CS_CITIES.map(c => ({
        path: `cs/navrh-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Brazilian Portuguese cities (Sprint C) ===
      ...PT_BR_CITIES.map(c => ({
        path: `pt-br/projeto-cftv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Dutch cities (Sprint D) ===
      ...NL_CITIES.map(c => ({
        path: `nl/cctv-ontwerp-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Hungarian cities (Sprint F) ===
      // 3 HU cities under /hu/cctv-tervezes-{slug}. HU = 2 Stripe paid subs,
      // 2.8% MRR, 30 GSC clicks/90d — paying market with zero competitor coverage.
      ...HU_CITIES.map(c => ({
        path: `hu/cctv-tervezes-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Lithuanian cities (Sprint F) ===
      // 3 LT cities under /lt/cctv-projektavimas-{slug}. LT = 4.6% MRR
      // (top 7 globally), 2 Stripe subs, 14 GSC clicks/90d. Zero competitor coverage.
      ...LT_CITIES.map(c => ({
        path: `lt/cctv-projektavimas-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Romanian cities (Sprint G — 2026-05-25) ===
      // 4 RO cities under /ro/proiectare-cctv-{slug}. RO = 1 paying sub,
      // 32 GSC clicks / 792 impressions per 90d. EU market with zero
      // competitor coverage in Romanian language.
      ...RO_CITIES.map(c => ({
        path: `ro/proiectare-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Programmatic SKU comparison pages (Day-3 sprint, top 200 model-vs-model long-tail) ===
      // EN-only intentionally — JVSG ranks for these queries primarily in English; per-lang
      // variants would 4×-multiply SSG output for marginal SEO gain.
      {
        path: 'compare/sku/:slug',
        element: <SkuComparisonPage />,
        getStaticPaths: () => import('@/data/skuComparisonPairs.generated').then(m => m.SKU_COMPARISON_SLUGS.map(s => `compare/sku/${s}`)),
      },

      // === JVSG brand-jacking + standards + verticals (SEO push) ===
      { path: 'en-62676-4-calculator', element: <EN62676Calculator /> },
      { path: 'johnson-criteria-calculator', element: <JohnsonCriteriaCalculator /> },
      { path: 'ndaa-cctv-compliance', element: <NDAACompliance /> },
      // Brand-jacking vanity redirects — capture URL-bar typing of "jvsg-*" guesses.
      // Not added to sitemap (would create duplicate-content risk); pure redirects.

      // === Vendor-bypass alternative landing pages (Day-2 sprint, multi-vendor SEO) ===

      // === Detection-sensor SEO landings (Phase 5, 2026-05-13) ===

      // === Vertical JVSG-alternative landing pages (exact-match SEO) ===

      // === IPVM-alternative + CCTVDesignTool-alternative (v1.7.5 SEO sprint) ===


      // === Other public pages ===
      { path: 'glossary', element: <Glossary /> },
      { path: 'pricing', element: <Pricing /> },
      { path: 'rodo', element: <RODO /> },
      { path: 'camera-physics', element: <CameraPhysics /> },
      { path: 'changelog', element: <LangWrapper><Changelog /></LangWrapper> },
      { path: 'unsubscribed', element: <Unsubscribed /> },


      // /attributions — CC-BY 3D-model credits (licence compliance). EN-only,
      // language-neutral list of author/source credits.
      { path: 'attributions', element: <AttributionsPage /> },
      // /compliance — country-by-country CCTV regulation pillar (Sprint G
      // 2026-05-21). EN-only with cross-links to native city pages.

      // === Redirects for removed pages ===

      // === Client Portal (public, no auth) ===
      { path: 'p/:token', element: <Portal /> },

      // === Team invite acceptance (public — session comes from the email link) ===
      { path: 'accept-invite', element: <AcceptInvite /> },

      // === Demo project (public, read-only) ===
      {
        path: 'demo',
        element: (
          <ErrorBoundary name="demo">
            <div className="h-screen w-screen">
              <CCTVDesignerV2 demoProjectId="ef6b455c-557b-4719-8717-c0114eb9fa92" />
            </div>
          </ErrorBoundary>
        ),
      },

      // === Anonymous editable sandbox (public, noindex, CSR-only) ===
      // /try?camera=<slug> seeds the viewed camera on an empty map; anon edits,
      // work persists, hydrates as their first project on signup. NOT in sitemap.
      {
        path: 'try',
        element: (
          <ErrorBoundary name="try-sandbox">
            <TrySandbox />
          </ErrorBoundary>
        ),
      },

      // === Protected routes (excluded from SSG via includedRoutes) ===
      {
        path: 'dashboard',
        element: <ProtectedRoute><ErrorBoundary name="dashboard"><Dashboard /></ErrorBoundary></ProtectedRoute>,
      },
      {
        path: 'organization',
        element: <ProtectedRoute><ErrorBoundary name="org-admin"><OrgAdminConsole /></ErrorBoundary></ProtectedRoute>,
      },
      {
        path: 'settings',
        element: <ProtectedRoute><ErrorBoundary name="settings"><Settings /></ErrorBoundary></ProtectedRoute>,
      },
      {
        path: 'checkout-success',
        element: <ProtectedRoute><CheckoutSuccess /></ProtectedRoute>,
      },
      {
        path: 'cctv-design',
        element: (
          <ProtectedRoute>
            <ErrorBoundary name="designer">
              <div className="h-screen w-screen">
                <CCTVDesignerV2 />
              </div>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
      },
      // === API Docs (Premium only) ===
      {
        path: 'api-docs',
        element: <ProtectedRoute><ApiDocs /></ProtectedRoute>,
      },

      // === 404 ===
      { path: '404', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];
